import { Event } from './Event'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useTw } from '../../../../../../utils/Styles'
import { ConversationQuery } from '../Conversation.generated'
import { useIntersectionObserver, usePrevious } from '../../../../../../utils'

type Props = {
  done: boolean
  loading: boolean
  onNext: () => void
  data?: ConversationQuery['Conversation']['messages']
}

function ConversationWithContent({
  data = [],
  onNext,
  done,
}: Omit<Props, 'loading'>) {
  const tw = useTw()
  const [t] = useTranslation('common')
  const endRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const lastId = usePrevious(data?.[0]?._id)

  // This function scrolls when receiving messages
  useEffect(() => {
    if (scrollRef.current && data?.[0]?._id !== lastId)
      scrollRef.current.scroll({ top: 0, behavior: 'smooth' })
  }, [scrollRef, lastId, data])

  // This hook pulls more messages
  useIntersectionObserver({
    ref: endRef,
    threshold: 0.1,
    callback: (entries) => {
      if (entries[0].isIntersecting) {
        onNext()
      }
    },
  })

  return (
    <div
      ref={scrollRef}
      className={tw`relative flex flex-grow flex-col-reverse overflow-y-auto`}
    >
      {done ? (
        <div className={tw`relative py-4`}>
          <div
            aria-hidden="true"
            className={tw`absolute top-0 bottom-0 left-4 right-4 flex items-center`}
          >
            <div className={tw`w-full border-t border-neutral-300`} />
          </div>
          <div className={tw`relative flex justify-center`}>
            <span className={tw`px-2 bg-gray-50 text-sm text-neutral-400`}>
              {t('conversation.convo_done')}
            </span>
          </div>
        </div>
      ) : null}
      {data.map((_) => (
        <Event key={_._id} event={_} />
      ))}
      <div
        ref={endRef}
        className={tw`w-full -mb-52 pt-52 pointer-events-none`}
      />
    </div>
  )
}

export function Conversation({ loading, onNext, data, done }: Props) {
  const tw = useTw()
  return loading && !data ? (
    <div className={tw`flex flex-grow flex-col-reverse overflow-y-auto`} />
  ) : (
    <ConversationWithContent data={data} onNext={onNext} done={done} />
  )
}
